import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Box
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  RadioButtonUnchecked as OptionalIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { ZulassungsantragResponse } from '../../types/antrag';
import { Verwertungsnachweis } from '../../types/fahrzeug';

interface RequiredDataChecklistProps {
  antrag: ZulassungsantragResponse;
  vollmachtExists: boolean;
  sepaExists: boolean;
  evbNummer?: string;
  verwertungsnachweis?: Verwertungsnachweis | null;
}

interface ChecklistItem {
  label: string;
  isOptional: boolean;
  isFulfilled: boolean;
  note?: string;
}

// Hilfsfunktion: Prüft ob ein Wert vorhanden ist
const hasValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return false;
};

// Generiert die Checkliste basierend auf Vorfalltyp und Antragstellertyp
const getChecklistItems = (
  antrag: ZulassungsantragResponse,
  vollmachtExists: boolean,
  sepaExists: boolean,
  evbNummer?: string,
  verwertungsnachweis?: Verwertungsnachweis | null
): ChecklistItem[] => {
  const geschaeftsvorfallTyp = antrag.geschaeftsvorfallTyp;
  const antragstellerTyp = antrag.antragstellerTyp || 'EIGENE_PERSON';
  const isAufDritte = antragstellerTyp === 'DRITTE_PERSON';

  // Gemeinsame Prüfungen
  const hasFIN = hasValue(antrag.fahrzeug?.fin);
  const hasHalter = hasValue(antrag.halter);
  const hasHalterName = hasValue(antrag.halter?.vorname) || hasValue(antrag.halter?.nachname) || hasValue(antrag.halter?.firma);
  // Bankverbindung: IBAN aus Halter-Objekt, Kontoinhaber = Halter-Name
  const hasBankverbindung = hasValue(antrag.halter?.iban) && hasHalterName;
  const hasAltesKennzeichen = hasValue(antrag.fahrzeug?.altesKennzeichen) || hasValue(antrag.fahrzeug?.altesKennzeichenVorn) || hasValue(antrag.fahrzeug?.altesKennzeichenHinten);
  const hasAltesKennzeichenSicherheitscode = hasValue(antrag.fahrzeug?.altesKennzeichen?.siegelSicherheitscode) || hasValue(antrag.fahrzeug?.altesKennzeichenVorn?.siegelSicherheitscode) || hasValue(antrag.fahrzeug?.altesKennzeichenHinten?.siegelSicherheitscode);
  const hasZB1 = hasValue(antrag.fahrzeug?.zb1Nummer);
  const hasZB1Sicherheitscode = hasValue(antrag.fahrzeug?.zb1Sicherheitscode);
  const hasZB2 = hasValue(antrag.fahrzeug?.zb2Nummer);
  const hasZB2Sicherheitscode = hasValue(antrag.fahrzeug?.zb2Sicherheitscode);
  const hasVersicherung = hasValue(evbNummer); // eVB-Nummer aus Versicherung
  const hasKennzeichen = hasValue(antrag.fahrzeug?.neuesKennzeichen) || hasValue(antrag.reservierungsPin);
  const hasVerwendungszweck = hasValue(antrag.fahrzeug?.verwendungszweck);
  // Verwertungsnachweis prüfen - mindestens ein Feld muss ausgefüllt sein
  const hasVerwertungsnachweis = verwertungsnachweis && (
    hasValue(verwertungsnachweis.betriebsnummerDemontagebetrieb) ||
    hasValue(verwertungsnachweis.datumAusstellung) ||
    hasValue(verwertungsnachweis.merkmalVerwertungsnachweis) ||
    hasValue(verwertungsnachweis.staatVerwertung)
  );
  // Fahrzeugbezogene Kennzeichenreservierung (Boolean-Feld bei Außerbetriebsetzung)
  const hasKennzeichenReservierung = antrag.kennzeichenReservierung === true;
  // Großkunde-Status ist aktuell nicht im Kunde-Objekt vorhanden
  const isGrosskunde = true; // TODO: Feld hinzufügen wenn im Backend verfügbar

  let items: ChecklistItem[] = [];

  // Hilfsfunktion: Prüft ob es sich um eine Umschreibung handelt
  const isUmschreibung = geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL' ||
                         geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL' ||
                         geschaeftsvorfallTyp === 'UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL';

  // Hilfsfunktion: Prüft ob es sich um eine Halteränderung handelt
  const isHalteraenderung = geschaeftsvorfallTyp === 'AENDERUNG_HALTERDATEN';

  switch (true) {
    // 1. Außerbetriebsetzung
    case geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG':
      items = [
        { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
        { label: 'Altes Kennzeichen mit Sicherheitscode', isOptional: false, isFulfilled: hasAltesKennzeichen && hasAltesKennzeichenSicherheitscode },
        // ZB1: nur Sicherheitscode ist erforderlich (Nummer ist optional)
        { label: 'Zulassungsbescheinigung Teil 1 mit Sicherheitscode', isOptional: false, isFulfilled: hasZB1Sicherheitscode },
        { label: 'Fahrzeugbezogene Kennzeichenreservierung', isOptional: true, isFulfilled: hasKennzeichenReservierung },
        { label: 'Verwertungsnachweis', isOptional: true, isFulfilled: !!hasVerwertungsnachweis },
        // ZB2 ist Pflicht wenn Verwertungsnachweis vorhanden, sonst optional
        // Nur Sicherheitscode ist erforderlich (Nummer ist optional)
        { 
          label: 'Zulassungsbescheinigung Teil 2', 
          isOptional: !hasVerwertungsnachweis, 
          isFulfilled: hasZB2Sicherheitscode, 
          note: 'Erforderlich bei Verwertungsnachweis' 
        },
      ];
      break;

    // 2. & 3. Neuzulassung
    case geschaeftsvorfallTyp === 'NEUZULASSUNG':
      if (isAufDritte) {
        // 3. Neuzulassung auf Dritte
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Bankverbindung', isOptional: false, isFulfilled: hasBankverbindung },
          { label: 'Dokument Vollmacht', isOptional: false, isFulfilled: vollmachtExists },
          { label: 'Dokument SEPA Mandat', isOptional: false, isFulfilled: sepaExists },
          { label: 'Halter', isOptional: false, isFulfilled: hasHalterName },
          { label: 'Bevollmächtigter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      } else {
        // 2. Neuzulassung auf sich selbst
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Halter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      }
      break;

    // 4. Wiederzulassung selber Halter (WZ) - Halter und Bankverbindung sind Pflicht
    case geschaeftsvorfallTyp === 'WIEDERZULASSUNG_SELBER_HALTER':
      items = [
        { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
        { label: 'Halter', isOptional: false, isFulfilled: hasHalterName },
        { label: 'Bankverbindung', isOptional: false, isFulfilled: hasBankverbindung },
        { label: 'Halter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
        { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
        { label: 'Altes Kennzeichen', isOptional: false, isFulfilled: hasAltesKennzeichen },
        { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
        { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
        { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
        { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
      ];
      break;

    // 5. & 6. Wiederzulassung (WG) - allgemeine Wiederzulassung über GKS
    case geschaeftsvorfallTyp === 'WIEDERZULASSUNG':
      if (isAufDritte) {
        // 6. Wiederzulassung auf Dritte
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Bankverbindung', isOptional: false, isFulfilled: hasBankverbindung },
          { label: 'Dokument Vollmacht', isOptional: false, isFulfilled: vollmachtExists },
          { label: 'Dokument SEPA Mandat', isOptional: false, isFulfilled: sepaExists },
          { label: 'Halter', isOptional: false, isFulfilled: hasHalterName },
          { label: 'Bevollmächtigter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Altes Kennzeichen', isOptional: false, isFulfilled: hasAltesKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      } else {
        // 5. Wiederzulassung auf sich selbst
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Halter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Altes Kennzeichen', isOptional: false, isFulfilled: hasAltesKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      }
      break;

    // 6. & 7. Umschreibung
    case isUmschreibung:
      if (isAufDritte) {
        // 7. Umschreibung auf Dritte
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Bankverbindung', isOptional: false, isFulfilled: hasBankverbindung },
          { label: 'Dokument Vollmacht', isOptional: false, isFulfilled: vollmachtExists },
          { label: 'Dokument SEPA Mandat', isOptional: false, isFulfilled: sepaExists },
          { label: 'Halter', isOptional: false, isFulfilled: hasHalterName },
          { label: 'Bevollmächtigter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Altes Kennzeichen', isOptional: false, isFulfilled: hasAltesKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      } else {
        // 6. Umschreibung auf sich selbst
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Halter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Kennzeichen (Wunsch- oder nächstes freies)', isOptional: false, isFulfilled: hasKennzeichen },
          { label: 'Altes Kennzeichen', isOptional: false, isFulfilled: hasAltesKennzeichen },
          { label: 'Versicherungsbestätigung (eVB)', isOptional: false, isFulfilled: hasVersicherung },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
          { label: 'Zulassungsbescheinigung Teil 2', isOptional: false, isFulfilled: hasZB2 },
          { label: 'Fahrzeugverwendung', isOptional: true, isFulfilled: hasVerwendungszweck },
        ];
      }
      break;

    // 8. & 9. Halteränderung
    case isHalteraenderung:
      if (isAufDritte) {
        // 9. Halteränderung auf Dritte
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Dokument Vollmacht', isOptional: false, isFulfilled: vollmachtExists },
          { label: 'Halter', isOptional: false, isFulfilled: hasHalterName },
          { label: 'Altes Kennzeichen mit Sicherheitscode', isOptional: false, isFulfilled: hasAltesKennzeichen && hasAltesKennzeichenSicherheitscode },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
        ];
      } else {
        // 8. Halteränderung auf sich selbst
        items = [
          { label: 'FIN', isOptional: false, isFulfilled: hasFIN },
          { label: 'Halter ist Großkunde', isOptional: false, isFulfilled: isGrosskunde },
          { label: 'Altes Kennzeichen mit Sicherheitscode', isOptional: false, isFulfilled: hasAltesKennzeichen && hasAltesKennzeichenSicherheitscode },
          { label: 'Zulassungsbescheinigung Teil 1', isOptional: false, isFulfilled: hasZB1 },
        ];
      }
      break;

    default:
      items = [
        { label: 'Unbekannter Geschäftsvorfalltyp: ' + geschaeftsvorfallTyp, isOptional: false, isFulfilled: false },
      ];
  }

  return items;
};

const RequiredDataChecklist: React.FC<RequiredDataChecklistProps> = ({
  antrag,
  vollmachtExists,
  sepaExists,
  evbNummer,
  verwertungsnachweis
}) => {
  const items = getChecklistItems(antrag, vollmachtExists, sepaExists, evbNummer, verwertungsnachweis);
  
  const requiredItems = items.filter(item => !item.isOptional);
  const optionalItems = items.filter(item => item.isOptional);
  const fulfilledRequired = requiredItems.filter(item => item.isFulfilled).length;
  const totalRequired = requiredItems.length;
  const allRequiredFulfilled = fulfilledRequired === totalRequired;

  return (
    <Card 
      elevation={0} 
      sx={{ 
        mb: 3,
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
        border: '1px dashed',
        borderColor: 'info.main',
        borderRadius: 2
      }}
    >
      <CardHeader
        avatar={<InfoIcon color="info" />}
        title="Benötigte Daten"
        titleTypographyProps={{ 
          variant: 'subtitle1', 
          fontWeight: 'bold',
          color: 'info.main'
        }}
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body2" color="textSecondary">
              Pflichtfelder: {fulfilledRequired} von {totalRequired} erfüllt
            </Typography>
            {allRequiredFulfilled ? (
              <Chip label="Vollständig" color="success" size="small" />
            ) : (
              <Chip label="Unvollständig" color="warning" size="small" />
            )}
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {/* Pflichtfelder */}
          {requiredItems.map((item, index) => (
            <ListItem key={`required-${index}`} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.isFulfilled ? (
                  <CheckIcon color="success" fontSize="small" />
                ) : (
                  <MissingIcon color="error" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      color: item.isFulfilled ? 'success.main' : 'error.main',
                      fontWeight: item.isFulfilled ? 'normal' : 'medium'
                    }}
                  >
                    {item.label}
                  </Typography>
                }
                secondary={item.note}
              />
            </ListItem>
          ))}

          {/* Optionale Felder */}
          {optionalItems.length > 0 && (
            <>
              <ListItem sx={{ py: 0.5, mt: 1 }}>
                <ListItemText
                  primary={
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                      Optionale Angaben
                    </Typography>
                  }
                />
              </ListItem>
              {optionalItems.map((item, index) => (
                <ListItem key={`optional-${index}`} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {item.isFulfilled ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <OptionalIcon color="disabled" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: item.isFulfilled ? 'success.main' : 'text.secondary'
                        }}
                      >
                        {item.label}
                      </Typography>
                    }
                    secondary={item.note}
                  />
                </ListItem>
              ))}
            </>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default RequiredDataChecklist;
